import {
  DndContext,
  DragOverlay,
  closestCorners,
  PointerSensor,
  KeyboardSensor,
  useSensors,
  useSensor,
  useDroppable,
  type DragEndEvent,
  type DragOverEvent,
  type DragStartEvent,
} from "@dnd-kit/core";
import {
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useEffect, useMemo, useRef, useState } from "react";
import { api } from "@/lib/api";
import { invalidateForTask } from "@/lib/useTaskMutations";
import { queryKeys } from "@/lib/queryKeys";
import { TaskCard } from "./TaskCard";
import { Avatar } from "./Avatar";
import { Icon } from "./Icon";
import { toast } from "./Toast";
import { useEffortUnit } from "@/lib/effort";
import { moveBetween } from "@/lib/kanban/moveBetween";
import {
  makeCellId,
  isCellId,
  resolveSwimlaneDrop,
} from "@/lib/kanban/resolveSwimlaneDrop";
import { TASK_STATUS_LABEL } from "@/lib/taskStatus";
import { priorityLabel, priorityColorVar } from "@/lib/taskPriority";
import { typeLabel } from "@/lib/workItemType";
import type { KanbanTask } from "./KanbanBoard";

const COLUMNS = ["backlog", "todo", "doing", "done"] as const;
type ColumnId = (typeof COLUMNS)[number];

const STATUS_META: Record<ColumnId, { label: string; color: string }> = {
  backlog: { label: TASK_STATUS_LABEL.backlog, color: "var(--st-backlog)" },
  todo: { label: TASK_STATUS_LABEL.todo, color: "var(--st-todo)" },
  doing: { label: TASK_STATUS_LABEL.doing, color: "var(--st-doing)" },
  done: { label: TASK_STATUS_LABEL.done, color: "var(--st-done)" },
};

/** Dimension a board can be split into horizontal lanes by. */
export type LaneBy = "assignee" | "priority" | "milestone" | "type" | "epic";

type Lane = { key: string; label: string; assigneeId?: string };

// Cell-key helpers live in resolveSwimlaneDrop (shared with its unit tests).
const cellKey = (laneKey: string, status: ColumnId): string =>
  makeCellId(laneKey, status);
const isCellKey = (id: string): boolean => isCellId(id);

const UNASSIGNED = "__unassigned__";
const NONE = "__none__";

export type SwimlaneBoardProps = {
  projectId: string;
  tasks: KanbanTask[];
  users?: Record<string, { name: string; image?: string | null }>;
  laneBy: LaneBy;
  /** epicId -> name for epic lane labels */
  epicNames?: ReadonlyMap<string, string>;
  /** milestoneId -> { name, sortOrder } for milestone lane labels + ordering */
  milestoneNames?: ReadonlyMap<string, string>;
  onAddTaskInColumn?: (status: ColumnId, lane: Lane) => void;
};

/**
 * Swimlane board — true 2D lanes. Each lane is a horizontal row spanning all
 * four status columns; cards drag across columns *within* a lane (which changes
 * status) but cannot cross into another lane (the lane dimension —
 * assignee/priority/milestone — isn't mutated by a board drag). Lanes collapse
 * and show a card count.
 *
 * Distinct from KanbanBoard's read-only lane modes: here every cell is a
 * droppable + sortable, wired through the same optimistic patch +
 * invalidateForTask pipeline the status board uses.
 */
export function SwimlaneBoard({
  projectId,
  tasks,
  users = {},
  laneBy,
  epicNames,
  milestoneNames,
  onAddTaskInColumn,
}: SwimlaneBoardProps) {
  const qc = useQueryClient();
  const effortUnit = useEffortUnit();

  // Subtasks never render as board cards (parity with KanbanBoard / T42).
  const topLevel = useMemo(
    () => tasks.filter((t) => t.parentTaskId == null),
    [tasks],
  );

  // Lane definitions (ordered, only lanes that contain ≥1 task) + the
  // function that maps a task to its lane key.
  const { lanes, laneOf } = useMemo(
    () => buildLanes(topLevel, laneBy, { users, epicNames, milestoneNames }),
    [topLevel, laneBy, users, epicNames, milestoneNames],
  );

  const buildItems = useMemo(
    () =>
      (src: KanbanTask[]): Record<string, KanbanTask[]> => {
        const m: Record<string, KanbanTask[]> = {};
        for (const lane of lanes)
          for (const s of COLUMNS) m[cellKey(lane.key, s)] = [];
        for (const t of src) {
          const laneKey = laneOf(t);
          const s: ColumnId = (COLUMNS as readonly string[]).includes(t.status)
            ? (t.status as ColumnId)
            : "backlog";
          const key = cellKey(laneKey, s);
          (m[key] ??= []).push(t);
        }
        for (const k of Object.keys(m))
          m[k]!.sort((a, b) => a.sortOrder - b.sortOrder);
        return m;
      },
    [lanes, laneOf],
  );

  const [items, setItems] = useState<Record<string, KanbanTask[]>>(() =>
    buildItems(topLevel),
  );
  const isDraggingRef = useRef(false);
  useEffect(() => {
    if (!isDraggingRef.current) setItems(buildItems(topLevel));
  }, [topLevel, buildItems]);

  const [collapsed, setCollapsed] = useState<Set<string>>(new Set());
  const [activeTask, setActiveTask] = useState<KanbanTask | null>(null);
  const [dragOverCell, setDragOverCell] = useState<string | null>(null);
  const [rejectingId, setRejectingId] = useState<string | null>(null);
  const snapshotRef = useRef<Record<string, KanbanTask[]> | null>(null);

  const patch = useMutation({
    mutationFn: ({
      taskId,
      status,
      prevId,
      nextId,
    }: {
      taskId: string;
      status?: ColumnId;
      prevId: string | null;
      nextId: string | null;
    }) => api.patchTask(taskId, { status, prevId, nextId }),
    onMutate: async (v) => {
      await qc.cancelQueries({ queryKey: queryKeys.tasksFor(projectId) });
      const prev = qc.getQueryData<{ tasks: KanbanTask[] }>(
        queryKeys.tasksFor(projectId),
      );
      if (v.status) {
        qc.setQueryData<{ tasks: KanbanTask[] }>(
          queryKeys.tasksFor(projectId),
          (old) =>
            old
              ? {
                  tasks: old.tasks.map((t) =>
                    t.id === v.taskId ? { ...t, status: v.status! } : t,
                  ),
                }
              : old,
        );
      }
      return { prev };
    },
    onError: (_e, v, ctx) => {
      if (ctx?.prev) qc.setQueryData(queryKeys.tasksFor(projectId), ctx.prev);
      toast.error("Couldn't move task", {
        action: { label: "Retry", onClick: () => patch.mutate(v) },
      });
    },
    onSettled: (_d, _e, v) =>
      invalidateForTask(qc, { projectId, taskId: v.taskId }),
  });

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 8 } }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    }),
  );

  function findCell(taskId: string): string | null {
    for (const k of Object.keys(items))
      if (items[k]!.some((t) => t.id === taskId)) return k;
    return null;
  }

  function onDragStart(e: DragStartEvent) {
    isDraggingRef.current = true;
    const id = e.active.id as string;
    setActiveTask(topLevel.find((t) => t.id === id) ?? null);
    snapshotRef.current = items;
    setDragOverCell(findCell(id));
  }

  function onDragOver(e: DragOverEvent) {
    const { active, over } = e;
    if (!over) {
      setDragOverCell(null);
      return;
    }
    const activeId = active.id as string;
    const overId = over.id as string;
    const sourceCell = findCell(activeId);
    const destCell = isCellKey(overId) ? overId : findCell(overId);
    if (!sourceCell || !destCell) return;
    setDragOverCell(destCell);
    if (sourceCell === destCell && activeId === overId) return;
    setItems((prev) =>
      moveBetween(prev, activeId, sourceCell, destCell, overId),
    );
  }

  function onDragEnd(e: DragEndEvent) {
    isDraggingRef.current = false;
    setDragOverCell(null);
    const { active, over } = e;
    const activeId = active.id as string;
    const task = activeTask;
    const snapshot = snapshotRef.current;
    snapshotRef.current = null;
    setActiveTask(null);
    if (!task || !snapshot) return;

    if (!over) {
      setItems(snapshot);
      return;
    }

    const verdict = resolveSwimlaneDrop(snapshot, items, activeId);
    if (verdict.type === "noop") return;

    // Lane constraint: a board drag changes status (column), never the lane
    // dimension. Reject cross-lane drops and bounce the card back.
    if (verdict.type === "reject") {
      setItems(snapshot);
      setRejectingId(task.id);
      toast.error(
        "Cards stay in their lane — drag across columns to change status.",
      );
      setTimeout(() => setRejectingId(null), 500);
      return;
    }

    patch.mutate({
      taskId: task.id,
      status: (verdict.status as ColumnId | null) ?? undefined,
      prevId: verdict.prevId,
      nextId: verdict.nextId,
    });
  }

  function onDragCancel() {
    isDraggingRef.current = false;
    setDragOverCell(null);
    if (snapshotRef.current) setItems(snapshotRef.current);
    snapshotRef.current = null;
    setActiveTask(null);
  }

  function toggleLane(key: string) {
    setCollapsed((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  }

  if (lanes.length === 0) {
    return (
      <div className="swim-empty t-muted">No tasks to lay out in lanes.</div>
    );
  }

  return (
    <DndContext
      sensors={sensors}
      collisionDetection={closestCorners}
      onDragStart={onDragStart}
      onDragOver={onDragOver}
      onDragEnd={onDragEnd}
      onDragCancel={onDragCancel}
      autoScroll={{ acceleration: 8, threshold: { x: 0.15, y: 0.1 } }}
    >
      <div className="swim-scroll" role="region" aria-label="Project swimlanes">
        <div className="swim">
          <div className="swim-colhead" role="row">
            {COLUMNS.map((s) => (
              <div key={s} className="swim-ch">
                <span
                  className="ch-dot"
                  style={{ background: STATUS_META[s].color }}
                />
                <span className="ch-name">{STATUS_META[s].label}</span>
              </div>
            ))}
          </div>

          {lanes.map((lane) => {
            const laneTotal = COLUMNS.reduce(
              (n, s) => n + (items[cellKey(lane.key, s)]?.length ?? 0),
              0,
            );
            const isCollapsed = collapsed.has(lane.key);
            return (
              <div key={lane.key} className="swim-lane" data-lane={lane.key}>
                <button
                  type="button"
                  className="swim-lane-head"
                  aria-expanded={!isCollapsed}
                  onClick={() => toggleLane(lane.key)}
                >
                  <span
                    className={`swim-chev${isCollapsed ? " collapsed" : ""}`}
                  >
                    <Icon name="chev" size={14} />
                  </span>
                  {lane.assigneeId && lane.assigneeId !== UNASSIGNED && (
                    <Avatar
                      id={lane.assigneeId}
                      name={lane.label}
                      size={22}
                      image={users[lane.assigneeId]?.image}
                    />
                  )}
                  {laneBy === "priority" && (
                    <span
                      className="swim-prio-dot"
                      style={{ background: priorityColorVar(Number(lane.key)) }}
                      aria-hidden="true"
                    />
                  )}
                  <span className="swim-lane-name">{lane.label}</span>
                  <span className="swim-lane-count">{laneTotal}</span>
                </button>
                {!isCollapsed && (
                  <div className="swim-row" role="row">
                    {COLUMNS.map((s) => (
                      <SwimCell
                        key={s}
                        id={cellKey(lane.key, s)}
                        status={s}
                        tasks={items[cellKey(lane.key, s)] ?? []}
                        users={users}
                        isDropTarget={dragOverCell === cellKey(lane.key, s)}
                        rejectingId={rejectingId}
                        effortUnit={effortUnit}
                        onAddTask={
                          onAddTaskInColumn
                            ? () => onAddTaskInColumn(s, lane)
                            : undefined
                        }
                      />
                    ))}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>

      <DragOverlay
        dropAnimation={{
          duration: 200,
          easing: "cubic-bezier(0.18, 0.67, 0.6, 1.22)",
        }}
      >
        {activeTask ? (
          <div style={{ cursor: "grabbing" }}>
            <TaskCard
              task={activeTask}
              users={users}
              isDragOverlay
              effortUnit={effortUnit}
            />
          </div>
        ) : null}
      </DragOverlay>
    </DndContext>
  );
}

/** One lane×status cell: a droppable sortable column of cards. */
function SwimCell({
  id,
  status,
  tasks,
  users,
  isDropTarget,
  rejectingId,
  effortUnit,
  onAddTask,
}: {
  id: string;
  status: ColumnId;
  tasks: KanbanTask[];
  users: Record<string, { name: string; image?: string | null }>;
  isDropTarget: boolean;
  rejectingId: string | null;
  effortUnit?: ReturnType<typeof useEffortUnit>;
  onAddTask?: () => void;
}) {
  const { setNodeRef } = useDroppable({ id });
  return (
    <div
      ref={setNodeRef}
      className={`swim-cell${isDropTarget ? " drop-on" : ""}`}
      role="group"
      aria-label={`${STATUS_META[status].label}, ${tasks.length} tasks`}
    >
      <SortableContext
        items={tasks.map((t) => t.id)}
        strategy={verticalListSortingStrategy}
      >
        {tasks.map((t) => (
          <TaskCard
            key={t.id}
            task={t}
            users={users}
            rejecting={rejectingId === t.id}
            effortUnit={effortUnit}
          />
        ))}
      </SortableContext>
      {onAddTask && (
        <button
          type="button"
          className="swim-add"
          onClick={onAddTask}
          aria-label="Add task"
        >
          <Icon name="plus" size={12} />
        </button>
      )}
    </div>
  );
}

/** Derive ordered, non-empty lanes + a task→laneKey resolver for a dimension. */
function buildLanes(
  tasks: KanbanTask[],
  laneBy: LaneBy,
  ctx: {
    users: Record<string, { name: string; image?: string | null }>;
    epicNames?: ReadonlyMap<string, string>;
    milestoneNames?: ReadonlyMap<string, string>;
  },
): { lanes: Lane[]; laneOf: (t: KanbanTask) => string } {
  if (laneBy === "assignee") {
    const laneOf = (t: KanbanTask) =>
      t.assigneeIds.length > 0 ? t.assigneeIds[0]! : UNASSIGNED;
    const present = new Set(tasks.map(laneOf));
    const assigned = [...present]
      .filter((k) => k !== UNASSIGNED)
      .map((k) => ({ key: k, label: ctx.users[k]?.name ?? k, assigneeId: k }))
      .sort((a, b) => a.label.localeCompare(b.label));
    const lanes: Lane[] = [...assigned];
    if (present.has(UNASSIGNED))
      lanes.push({
        key: UNASSIGNED,
        label: "Unassigned",
        assigneeId: UNASSIGNED,
      });
    return { lanes, laneOf };
  }

  if (laneBy === "priority") {
    const laneOf = (t: KanbanTask) => String(t.priority || 3);
    const present = new Set(tasks.map(laneOf));
    const lanes: Lane[] = [1, 2, 3, 4]
      .filter((p) => present.has(String(p)))
      .map((p) => ({ key: String(p), label: priorityLabel(p) }));
    return { lanes, laneOf };
  }

  if (laneBy === "milestone") {
    const laneOf = (t: KanbanTask) => t.milestoneId ?? NONE;
    const present = new Set(tasks.map(laneOf));
    const named = [...present]
      .filter((k) => k !== NONE)
      .map((k) => ({
        key: k,
        label: ctx.milestoneNames?.get(k) ?? "Milestone",
      }))
      .sort((a, b) => a.label.localeCompare(b.label));
    const lanes: Lane[] = [...named];
    if (present.has(NONE)) lanes.push({ key: NONE, label: "No milestone" });
    return { lanes, laneOf };
  }

  if (laneBy === "type") {
    const laneOf = (t: KanbanTask) => t.type ?? "task";
    const present = new Set(tasks.map(laneOf));
    const lanes = [...present]
      .map((k) => ({ key: k, label: typeLabel(k as never) }))
      .sort((a, b) => a.label.localeCompare(b.label));
    return { lanes, laneOf };
  }

  // epic
  const laneOf = (t: KanbanTask) => t.epicId ?? NONE;
  const present = new Set(tasks.map(laneOf));
  const named = [...present]
    .filter((k) => k !== NONE)
    .map((k) => ({ key: k, label: ctx.epicNames?.get(k) ?? "Epic" }))
    .sort((a, b) => a.label.localeCompare(b.label));
  const lanes: Lane[] = [...named];
  if (present.has(NONE)) lanes.push({ key: NONE, label: "No epic" });
  return { lanes, laneOf };
}
